import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Settings,
  Languages,
  MessageSquare,
  Users,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Home,
  Globe,
  MapPin,
  FolderKanban,
  Briefcase
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_citrabi-tech/artifacts/oxx9leeo_%5BCITRA%5D%20Logo_No%20Background%20%281%29.png";

const AdminLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(DEFAULT_LOGO);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const response = await axios.get(`${API}/contact-settings`);
        if (response.data?.logo_url) {
          setCompanyLogo(response.data.logo_url);
        }
      } catch (error) {
        console.error('Error fetching company settings:', error);
      }
    };
    fetchCompanySettings();
  }, []);

  const menuItems = [
    { 
      path: '/admin', 
      icon: LayoutDashboard, 
      label: language === 'en' ? 'Dashboard Overview' : 'Ikhtisar Dashboard',
      exact: true
    },
    { 
      path: '/admin/services', 
      icon: Settings, 
      label: language === 'en' ? 'Manage Services' : 'Kelola Layanan'
    },
    { 
      path: '/admin/projects', 
      icon: FolderKanban, 
      label: language === 'en' ? 'Manage Projects' : 'Kelola Proyek'
    },
    { 
      path: '/admin/careers', 
      icon: Briefcase, 
      label: language === 'en' ? 'Manage Careers' : 'Kelola Karir'
    },
    { 
      path: '/admin/translations', 
      icon: Languages, 
      label: language === 'en' ? 'Manage Translations' : 'Kelola Terjemahan'
    },
    { 
      path: '/admin/inquiries', 
      icon: MessageSquare, 
      label: language === 'en' ? 'Lead Inquiries' : 'Pertanyaan Lead'
    },
    { 
      path: '/admin/contact-settings', 
      icon: MapPin, 
      label: language === 'en' ? 'Company Settings' : 'Pengaturan Perusahaan'
    },
    { 
      path: '/admin/users', 
      icon: Users, 
      label: language === 'en' ? 'User Management' : 'Manajemen Pengguna'
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-stone-200 px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-stone-100 rounded-md"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6 text-stone-700" />
          </button>
          <img src={companyLogo} alt="Logo" className="h-8 w-auto" />
        </div>
        <button 
          onClick={toggleLanguage}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-stone-100 hover:bg-stone-200 text-sm font-medium"
          data-testid="mobile-lang-toggle"
        >
          <Globe className="w-4 h-4" />
          {language.toUpperCase()}
        </button>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-stone-200 z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <img src={companyLogo} alt="Logo" className="h-8 w-auto" />
            <span className="font-heading font-bold text-stone-900 text-sm">Admin</span>
          </div>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1 hover:bg-stone-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  active 
                    ? 'bg-emerald-50 text-emerald-700 font-medium' 
                    : 'text-stone-600 hover:bg-stone-100'
                }`}
                data-testid={`nav-${item.path.split('/').pop() || 'dashboard'}`}
              >
                <Icon className={`w-5 h-5 ${active ? 'text-emerald-600' : ''}`} />
                <span className="text-sm">{item.label}</span>
                {active && <ChevronRight className="w-4 h-4 ml-auto text-emerald-600" />}
              </Link>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-stone-200">
          <Link 
            to="/"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-stone-600 hover:bg-stone-100 transition-colors mb-2"
            data-testid="view-website-link"
          >
            <Home className="w-5 h-5" />
            <span className="text-sm">{language === 'en' ? 'View Website' : 'Lihat Website'}</span>
          </Link>
          
          <div className="flex items-center gap-3 px-4 py-2 mb-3">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <span className="text-emerald-700 font-medium text-sm">
                {user?.name?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
              <p className="text-xs text-stone-500 truncate">{user?.email}</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            className="w-full justify-start gap-2 text-stone-600 hover:text-red-600 hover:border-red-200"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {language === 'en' ? 'Sign Out' : 'Keluar'}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Desktop Header */}
        <header className="hidden lg:flex items-center justify-between h-16 px-8 bg-white border-b border-stone-200">
          <h1 className="font-heading font-bold text-xl text-stone-900" data-testid="page-title">
            {title}
          </h1>
          <div className="flex items-center gap-4">
            <button 
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-stone-100 hover:bg-stone-200 text-sm font-medium transition-colors"
              data-testid="desktop-lang-toggle"
            >
              <Globe className="w-4 h-4" />
              {language.toUpperCase()}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
